import { memo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { components, OptionProps, SingleValueProps } from 'react-select';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.marginBase,
  },
  secondaryText: {
    ...theme.fonts.caption,
    color: theme.colors.inkTertiary,
    textAlign: 'right',
  },
  singleValue: {
    right: 0,
    left: 0,
  },
}));

interface OProps extends OptionProps<any, boolean> {
  secondaryLabelKeys?: string[] | string;
  translationPrefix?: string;
}

interface SProps extends SingleValueProps<any> {
  secondaryLabelKeys?: string[] | string;
  translationPrefix?: string;
}

interface CommonProps {
  secondaryLabelKeys?: string[] | string;
  translationPrefix?: string;
  data: any;
}

const useSecondaryLabelText = ({
  secondaryLabelKeys,
  translationPrefix,
  data,
}: CommonProps) => {
  const { t, i18n } = useTranslation();

  const keys = secondaryLabelKeys
    ? Array.isArray(secondaryLabelKeys)
      ? secondaryLabelKeys
      : [secondaryLabelKeys]
    : [];

  const values = keys
    .map((originalKey: string) => {
      const translate = originalKey.startsWith('translate:');
      const key = translate ? originalKey.substr(10) : originalKey;

      if (key in data) {
        if (translate) {
          const i18nKey = translationPrefix + data[key];
          if (i18n.exists(i18nKey)) {
            return t(i18nKey);
          }
        }
        return data[key];
      }
      return null;
    })
    .filter(Boolean);
  return values.join(', ');
};

export const OptionWithSecondaryText = memo(
  ({ children, secondaryLabelKeys, translationPrefix, ...props }: OProps) => {
    const theme: Theme = useTheme();
    const classes = useStyles({ theme });
    const secondaryLabelText = useSecondaryLabelText({
      secondaryLabelKeys,
      translationPrefix,
      data: props.data,
    });

    return (
      <components.Option {...props}>
        <div className={classes.container}>
          <>
            {children}
            {!!secondaryLabelText && (
              <div className={classes.secondaryText}>{secondaryLabelText}</div>
            )}
          </>
        </div>
      </components.Option>
    );
  },
);

export const SingleValueWithSecondaryText = ({
  children,
  secondaryLabelKeys,
  translationPrefix,
  ...props
}: SProps) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const secondaryLabelText = useSecondaryLabelText({
    secondaryLabelKeys,
    translationPrefix,
    data: props.data,
  });

  return (
    <components.SingleValue {...props} className={classes.singleValue}>
      <div className={classes.container}>
        <>
          {children}
          {!!secondaryLabelText && (
            <div className={classes.secondaryText}>{secondaryLabelText}</div>
          )}
        </>
      </div>
    </components.SingleValue>
  );
};
